import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  fillTermVariables,
  renderTermHtml,
  TERM_DOCUMENT_CSS,
} from '@/lib/terms/document';
import { TERM_VARIABLE_EXAMPLES } from '@/lib/terms/variables';
import { usePreviewTermPdf } from '@/services/term-versions';

interface TermPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  content: Record<string, unknown>;
}

/**
 * Pré-visualização com dados de exemplo no lugar das variáveis — mesmo render (`renderTermHtml` +
 * `fillTermVariables`) e mesmo CSS do PDF. O HTML vem do Tiptap (só nós conhecidos, texto
 * escapado), por isso pode ir no `dangerouslySetInnerHTML`.
 */
export function TermPreviewDialog({
  open,
  onOpenChange,
  title,
  content,
}: TermPreviewDialogProps) {
  const previewPdf = usePreviewTermPdf();
  const html = useMemo(() => {
    if (!open) return '';
    try {
      return fillTermVariables(renderTermHtml(content), TERM_VARIABLE_EXAMPLES);
    } catch {
      return '<p>Não foi possível montar a pré-visualização.</p>';
    }
  }, [open, content]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-[calc(210mm+64px)] overflow-hidden p-0 sm:max-w-[calc(210mm+64px)]">
        <DialogHeader className="border-b border-border px-6 py-5">
          <DialogTitle className="font-display text-[18px] font-extrabold uppercase tracking-tight text-white">
            Pré-visualizar — {title}
          </DialogTitle>
          <DialogDescription>
            Variáveis preenchidas com dados de exemplo. É assim que o termo sai no PDF.
          </DialogDescription>
          <Button
            variant="outline"
            size="md"
            icon="file-down"
            className="mt-3 self-start"
            isLoading={previewPdf.isPending}
            onClick={() => previewPdf.mutate({ title, content })}
          >
            Baixar PDF de exemplo
          </Button>
        </DialogHeader>
        <style>{TERM_DOCUMENT_CSS}</style>
        <div className="max-h-[calc(92vh-110px)] overflow-auto bg-sunken p-8">
          <div
            className="term-document mx-auto shadow-[var(--shadow-float)]"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML gerado pelo Tiptap a partir do schema, com texto escapado.
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
